import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import MobileBottomBar from "../components/MobileBottomBar";
import API from "../api/axiosConfig";
import { toast } from "react-toastify";
import { isSedeOpen } from "../utils/sedeStatus";
import { openMapsNavigation } from "../utils/openMapsNavigation";

const API_BASE = (process.env.REACT_APP_API_URL || "http://localhost:4000/api")
  .replace(/\/api\/?$/, "")
  .replace(/\/$/, "");

function buildLogoUrl(logoPath) {
  if (!logoPath) return null;
  return API_BASE + "/" + logoPath.replace(/^\//, "");
}

function normalizeSede(s) {
  return {
    ...s,
    _id: s.id ?? s._id,
    location: s.location ?? {
      lat: s.lat ? Number(s.lat) : null,
      lng: s.lng ? Number(s.lng) : null,
    },
    company: s.company ?? null,
  };
}

function normalizeRyder(r) {
  return {
    ...r,
    _id: r.id ?? r._id,
    lat: r.lat ? Number(r.lat) : null,
    lng: r.lng ? Number(r.lng) : null,
  };
}

function distanceKm(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const aa =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) *
      Math.cos((b.lat * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(aa), Math.sqrt(1 - aa));
}

const CACHE_KEY = "qk_lastPosition";

function loadCachedPosition() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// ── Icono punto azul pulsante (usuario) ─────────────────────
function makeUserDotIcon() {
  return L.divIcon({
    html: `
      <div style="position:relative;width:24px;height:24px;transform:translate(-50%,-50%)">
        <div style="position:absolute;inset:-6px;border-radius:50%;background:rgba(59,130,246,0.35);animation:qkPing 1.4s ease-in-out infinite"></div>
        <div style="width:24px;height:24px;border-radius:50%;background:#3b82f6;border:3px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>
      </div>`,
    className: "",
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

// ── Icono logo circular de sede ──────────────────────────────
function makeSedeIcon(logoUrl, open) {
  const border = open ? "3px solid #22c55e" : "3px solid #9ca3af";
  const opacity = open ? 1 : 0.65;
  const inner = logoUrl
    ? `<img src="${logoUrl}" style="width:100%;height:100%;object-fit:cover;border-radius:50%" onerror="this.outerHTML='<span style=font-size:22px>🏪</span>'" />`
    : `<span style="font-size:22px">🏪</span>`;
  return L.divIcon({
    html: `<div style="width:50px;height:50px;border-radius:50%;overflow:hidden;border:${border};background:#fff;box-shadow:0 2px 8px rgba(0,0,0,0.25);transform:translate(-50%,-50%);display:flex;align-items:center;justify-content:center;opacity:${opacity};cursor:pointer">${inner}</div>`,
    className: "",
    iconSize: [50, 50],
    iconAnchor: [25, 25],
  });
}

// ── Icono ryder ──────────────────────────────────────────────
function makeRyderIcon() {
  return L.divIcon({
    html: `<div style="font-size:28px;transform:translate(-50%,-50%);filter:drop-shadow(0 1px 3px rgba(0,0,0,0.4))">🛵</div>`,
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

// ── Controlador del mapa (seguir posición + capturar movimiento) ──
function MapController({ position, userMovedMap, onUserMove }) {
  const map = useMap();
  const prevPos = useRef(null);

  useMapEvents({
    dragstart: () => onUserMove(true),
    zoomstart: () => onUserMove(true),
  });

  useEffect(() => {
    if (!position || userMovedMap) return;
    if (
      prevPos.current &&
      Math.abs(prevPos.current.lat - position.lat) < 0.0001 &&
      Math.abs(prevPos.current.lng - position.lng) < 0.0001
    ) return;
    prevPos.current = position;
    map.setView([position.lat, position.lng], map.getZoom(), { animate: true });
  }, [position, userMovedMap, map]);

  return null;
}

// ── Ruta OSRM ────────────────────────────────────────────────
async function fetchRoute(origin, destination) {
  const res = await fetch(
    `https://router.project-osrm.org/route/v1/driving/` +
    `${origin.lng},${origin.lat};${destination.lng},${destination.lat}` +
    `?overview=full&geometries=geojson`
  );
  const data = await res.json();
  const route = data.routes?.[0];
  if (!route?.geometry?.coordinates) return null;
  // OSRM devuelve [lng, lat] — Leaflet necesita [lat, lng]
  return {
    coords: route.geometry.coordinates.map(([lng, lat]) => [lat, lng]),
    duration: route.duration,
  };
}

function fmtTime(secs) {
  const m = Math.round(secs / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)}h ${m % 60}min`;
}

// ── Componente principal ─────────────────────────────────────
export default function MapaPage() {
  const [position,     setPosition]     = useState(() => loadCachedPosition());
  const [sedes,        setSedes]        = useState([]);
  const [riders,       setRiders]       = useState([]);
  const [activeSede,   setActiveSede]   = useState(null);
  const [routeCoords,  setRouteCoords]  = useState([]);
  const [eta,          setEta]          = useState(null);
  const [userMovedMap, setUserMovedMap] = useState(false);
  const [gpsBlocked,   setGpsBlocked]   = useState(false);

  const watchIdRef   = useRef(null);
  const lastFetchPos = useRef(null);

  // ── Geolocalización continua ──
  useEffect(() => {
    if (!navigator.geolocation) { setGpsBlocked(true); return; }
    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        try { localStorage.setItem(CACHE_KEY, JSON.stringify(coords)); } catch {}
        setPosition((prev) => {
          if (!prev || Math.abs(prev.lat - coords.lat) > 0.0001 || Math.abs(prev.lng - coords.lng) > 0.0001) {
            return coords;
          }
          return prev;
        });
      },
      (err) => { if (err.code === 1) setGpsBlocked(true); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 5000 },
    );
    return () => { if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current); };
  }, []);

  // ── Cargar sedes y ryders ──
  useEffect(() => {
    if (!position) return;
    if (lastFetchPos.current && distanceKm(lastFetchPos.current, position) < 0.5) return;
    lastFetchPos.current = position;

    API.get(`/sedes/near?lat=${position.lat}&lng=${position.lng}&radiusKm=20`)
      .then((res) => {
        const raw = res.data?.data?.sedes ?? res.data?.sedes ?? res.data;
        setSedes(Array.isArray(raw) ? raw.map(normalizeSede) : []);
      })
      .catch(() => toast.error("Error cargando sedes"));

    API.get("/riders/active")
      .then((res) => {
        const raw =
          res.data?.data?.ryders ?? res.data?.data?.riders ??
          res.data?.ryders  ?? res.data?.riders  ?? res.data;
        setRiders(Array.isArray(raw) ? raw.map(normalizeRyder) : []);
      })
      .catch(() => {});
  }, [position]);

  // ── Calcular ruta a la sede activa ──
  useEffect(() => {
    if (!activeSede || !position) { setRouteCoords([]); setEta(null); return; }
    const dest = activeSede.location;
    if (!dest?.lat || !dest?.lng) return;
    fetchRoute(position, { lat: dest.lat, lng: dest.lng })
      .then((result) => {
        if (result) { setRouteCoords(result.coords); setEta(fmtTime(result.duration)); }
      })
      .catch(() => {});
  }, [activeSede, position]);

  const handleCloseSede = () => {
    setActiveSede(null);
    setRouteCoords([]);
    setEta(null);
  };

  // ── Sin GPS ──
  if (!position && gpsBlocked) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-60px)] gap-4 px-8 text-center">
        <span className="text-5xl">📍</span>
        <p className="text-gray-700 font-semibold text-lg">Ubicación no disponible</p>
        <p className="text-gray-500 text-sm">
          Permite el acceso a tu ubicación en la configuración del navegador para ver los comercios cercanos.
        </p>
      </div>
    );
  }

  // ── Esperando GPS (sin caché) ──
  if (!position) {
    return (
      <div className="flex flex-col items-center justify-center h-[calc(100vh-60px)] gap-3">
        <div className="w-10 h-10 border-4 border-supernova-400 border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-500">Obteniendo tu ubicación...</p>
      </div>
    );
  }

  return (
  <>
    <div className="relative z-0 w-full h-[calc(100vh-60px)] pb-20 lg:pb-0">
      <style>{`
        @keyframes qkPing {
          0%   { transform: scale(0.8); opacity: 0.8; }
          70%  { transform: scale(2);   opacity: 0; }
          100% { transform: scale(0.8); opacity: 0; }
        }
      `}</style>

      {/* Botón volver a mi ubicación */}
      {userMovedMap && (
        <button
          className="absolute top-4 right-4 z-[1000] bg-white shadow-lg px-3 py-2 rounded-lg text-sm font-semibold"
          onClick={() => setUserMovedMap(false)}
        >
          📍 Mi ubicación
        </button>
      )}

      <MapContainer
        center={[position.lat, position.lng]}
        zoom={14}
        style={{ height: "100%", width: "100%" }}
        zoomControl={true}
        attributionControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        <MapController
          position={position}
          userMovedMap={userMovedMap}
          onUserMove={setUserMovedMap}
        />

        {/* Punto azul del usuario */}
        <Marker
          position={[position.lat, position.lng]}
          icon={makeUserDotIcon()}
          zIndexOffset={1000}
        />

        {/* Sedes cercanas */}
        {sedes.map((sede) => {
          const loc = sede.location;
          if (!loc?.lat || !loc?.lng) return null;
          const status  = isSedeOpen(sede);
          const logoUrl = buildLogoUrl(sede.company?.logo);
          return (
            <Marker
              key={sede._id}
              position={[loc.lat, loc.lng]}
              icon={makeSedeIcon(logoUrl, status.open)}
              eventHandlers={{ click: () => setActiveSede(sede) }}
            />
          );
        })}

        {/* Ryders activos */}
        {riders.map((r) => {
          if (!r.lat || !r.lng) return null;
          return (
            <Marker key={r._id} position={[r.lat, r.lng]} icon={makeRyderIcon()} />
          );
        })}

        {/* Ruta a sede activa */}
        {routeCoords.length > 1 && (
          <Polyline positions={routeCoords} color="#2563eb" weight={5} opacity={0.85} />
        )}
      </MapContainer>

      {/* Panel info de sede seleccionada */}
      {activeSede && activeSede.location?.lat && (
        <div className="absolute bottom-24 lg:bottom-4 left-1/2 -translate-x-1/2 z-[1000] w-[calc(100%-32px)] max-w-sm bg-white rounded-2xl shadow-2xl p-4 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-bold text-gray-800 truncate">{activeSede.company?.name}</p>
              <p className="text-sm text-gray-600 truncate">{activeSede.name}</p>
              <p className="text-sm text-gray-500 truncate">{activeSede.address}</p>
              {eta && (
                <p className="text-xs font-semibold text-blue-600 mt-1">⏱️ ETA: {eta}</p>
              )}
            </div>
            <button
              onClick={handleCloseSede}
              className="shrink-0 text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
            >
              ✕
            </button>
          </div>
          <div className="flex gap-2">
            <button
              className="flex-1 bg-green-500 text-white px-3 py-2.5 rounded-xl text-sm font-semibold active:scale-95 transition-transform"
              onClick={() =>
                openMapsNavigation(
                  activeSede.location.lat,
                  activeSede.location.lng,
                  position?.lat,
                  position?.lng,
                )
              }
            >
              🧭 Cómo llegar
            </button>
            <button
              className="flex-1 bg-blue-500 text-white px-3 py-2.5 rounded-xl text-sm font-semibold active:scale-95 transition-transform"
              onClick={() => (window.location.href = `/sede/${activeSede._id}`)}
            >
              Ver sede
            </button>
          </div>
        </div>
      )}

    </div>
    <MobileBottomBar />
  </>
  );
}

